import type { PropsWithChildren } from 'react'
import { useSubcategory } from '~/entities/categories'
import {
  PRIOR_AUTH_PHASES,
  useAssignReviewer,
  useClassificationValidationReview,
  useCurrentDocument,
  useExtractedDataReviews,
} from '~/entities/document'
import { FLAG_TYPES, StepFlagButton, useStepFlagState } from '~/entities/document-flags'
import { usePayors } from '~/entities/payors'
import { trackQueueSelected } from '~/features/document-analytics'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { InfoTip, ScrollArea, SkeletonBlock, Text } from '~/shared/ui'

export function ReviewForm({
  children,
  step,
  loader,
  actions,
  customScroll,
}: PropsWithChildren<{
  step:
    | 'Classification'
    | 'Data Extraction'
    | 'Tagging'
    | 'Payment Deposit'
    | 'Duplication'
    | 'EOB Processing'
  loader?: React.ReactNode
  actions?: React.ReactNode
  customScroll?: boolean
}>) {
  const stepFlagTypeMap = {
    Classification: FLAG_TYPES.STEP_CLASSIFICATION,
    'Data Extraction': FLAG_TYPES.STEP_DATA,
    Tagging: FLAG_TYPES.STEP_CARC_RARC,
    'Payment Deposit': FLAG_TYPES.STEP_EOB_PAYMENT_DEPOSIT,
    Duplication: FLAG_TYPES.STEP_EOB_DUPLICATION,
    'EOB Processing': FLAG_TYPES.STEP_EOB_PROCESSING,
  } as const
  const stepFlag = useStepFlagState(stepFlagTypeMap[step])
  const isStepFlaggingEnabled = useIsFeatureEnabled('2026-05-step-flagging')
  const {
    data: { document },
  } = useCurrentDocument()
  const subcategory = useSubcategory({ category: document.category })
  const validationReview = useClassificationValidationReview({ documentKey: document.key })
  const isHideLlmClassificationEnabled = useIsFeatureEnabled('2026-08-hide-llm-classification')
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const { assigningInProgress } = useAssignReviewer(trackQueueSelected)
  const {
    data: { items: extractedDataReviews },
  } = useExtractedDataReviews({ documentKey: document.key })

  const payor = payors.find((p) => p.payorId === document.payor_id)

  const { sentinelElement, isScrolled } = useIsScrolled()

  return (
    <div className="flex h-full flex-col">
      <div className="bg-surface-low border-divider sticky top-0 z-1 flex h-[72px] shrink-0 items-center gap-8 border-b px-8 whitespace-nowrap">
        <div className="flex min-w-fit flex-col gap-0.5">
          <Text appearance="OVERLINE">Date Received</Text>
          <Text appearance="BODY2">
            {document.inbox_info?.date ? formatDateDefault(document.inbox_info.date) : ''}
          </Text>
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          {payor?.payorName ? (
            <>
              <Text appearance="OVERLINE">
                {extractedDataReviews.length > 0 ? 'Validated' : 'Suggested'} Payor Name
              </Text>
              <Text
                appearance="BODY2"
                truncated
                withTooltip
              >
                {payor?.payorName}
              </Text>
            </>
          ) : null}
        </div>
        <div className="flex min-w-fit flex-col gap-0.5">
          <Text appearance="OVERLINE">
            {validationReview ? 'Validated' : 'Suggested'} Classification
          </Text>
          <div className="flex items-center gap-1">
            {subcategory?.description && (
              <InfoTip>
                <Text
                  className="text-text-secondary box-border max-w-[280px] pr-6"
                  appearance="CAPTION"
                >
                  {subcategory?.description}
                </Text>
              </InfoTip>
            )}
            <Text
              appearance="BODY2"
              truncated
              withTooltip
            >
              {isHideLlmClassificationEnabled &&
              !validationReview &&
              PRIOR_AUTH_PHASES.includes(document.category_phase ?? '')
                ? subcategory?.metadata.phase.displayLabel || ''
                : subcategory?.displayLabel || document.category || ''}
            </Text>
          </div>
        </div>
      </div>
      {assigningInProgress && loader ? (
        <div>
          <div className="px-6 py-4">
            <SkeletonBlock className="h-[30px] w-[200px]" />
          </div>
          {loader}
        </div>
      ) : customScroll ? (
        <>
          <div className="bg-surface-lowest relative flex items-center px-6 py-4">
            <div className="flex items-center gap-4">
              <Text
                className="flex h-[30px] items-center"
                appearance="H6"
              >
                {step}
              </Text>
              {isStepFlaggingEnabled && (
                <StepFlagButton
                  flagType={stepFlagTypeMap[step]}
                  isCurrentStep={stepFlag.isCurrentStep}
                />
              )}
            </div>
            {actions && <div className="absolute top-4 right-6">{actions}</div>}
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </>
      ) : (
        <ScrollArea className="js-review-form-scroll-container h-full">
          <div className="flex flex-col">
            {sentinelElement}
            <div
              className={cn(
                'bg-surface-lowest sticky top-0 z-10 flex items-center justify-between px-6 py-4',
                {
                  'shadow-popover transition-shadow': isScrolled,
                },
              )}
            >
              <div className="flex items-center gap-4">
                <Text
                  className="flex h-[30px] items-center"
                  appearance="H6"
                >
                  {step}
                </Text>
                {isStepFlaggingEnabled && (
                  <StepFlagButton
                    flagType={stepFlagTypeMap[step]}
                    isCurrentStep={stepFlag.isCurrentStep}
                  />
                )}
              </div>
              {actions}
            </div>
            <div className="flex-1">{children}</div>
          </div>
        </ScrollArea>
      )}
    </div>
  )
}
